import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, ImagePlus, Loader2, Plus } from "@digital-twins/design-system/icons";
import type { CreativeMaterialRole } from "@digital-twins/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/Text";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import type { ProductProfileProps } from "@/types/creative-library";
import {
  CreativeEditorDialog,
  CreativeEditorSection,
} from "./CreativeEditorDialog";
import { CreativeUpload } from "./CreativeUpload";
import { BoardEditor } from "./BoardEditor";
import { CreativeDetail } from "./CreativeDetail";
import { ProductGallery } from "./ProductGallery";
import { PrivateImage } from "./PrivateImage";

export function ProductProfile({
  product,
  brand,
  creatives,
  boards,
  onClose,
  onSaved,
  onUse,
}: ProductProfileProps) {
  const { t } = useTranslation("app");
  const [saved, setSaved] = useState(product);
  const [name, setName] = useState(product.name);
  const [sku, setSku] = useState(product.sku ?? "");
  const [description, setDescription] = useState(product.description);
  const [linked, setLinked] = useState(product.boardTokens ?? []);
  const [board, setBoard] = useState("none");
  const [selected, setSelected] = useState<string[]>([]);
  const [preview, setPreview] = useState<(typeof creatives)[number] | null>(
    null,
  );
  const [upload, setUpload] = useState<CreativeMaterialRole | null>(null);
  const [newBoard, setNewBoard] = useState(false);
  const [tab, setTab] = useState("details");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const canEdit = product.canEdit && brand.canEdit;
  const allImages = creatives;
  const gallery = allImages.filter(
    (item) => item.productToken === product.token,
  );
  const applicable = boards.filter(
    (item) =>
      item.brandToken === product.brandToken ||
      item.applicableBrandTokens.includes(product.brandToken),
  );
  const chosen = applicable.find(
    (item) => item.token === board && linked.includes(item.token),
  );
  const fixed = [
    ...new Set([
      ...(chosen?.definition.referenceTokens ?? []),
      ...brand.materialTokens.filter((token) => {
        const item = allImages.find((image) => image.token === token);
        return (
          item &&
          item.role !== "product" &&
          chosen?.definition.slots.some(
            (slot) => slot.required && slot.role === item.role,
          )
        );
      }),
    ]),
  ];
  const used = [...new Set([...selected, ...fixed])];
  const missing = used.some(
    (token) => !allImages.some((item) => item.token === token),
  );
  const hasProduct = used.some((token) =>
    allImages.some(
      (item) =>
        item.token === token &&
        item.productToken === product.token &&
        item.role === "product",
    ),
  );
  const oldLinks = saved.boardTokens ?? [];
  const linksDirty =
    linked.length !== oldLinks.length ||
    linked.some((token) => !oldLinks.includes(token));
  const dirty =
    name !== saved.name ||
    sku !== (saved.sku ?? "") ||
    description !== saved.description ||
    linksDirty;
  async function save() {
    if (!canEdit || !name.trim() || saving) return;
    setSaving(true);
    setError(false);
    try {
      const updated = await creativeLibraryApi.updateProduct(product.token, {
        revision: saved.revision,
        name: name.trim(),
        sku: sku.trim() || null,
        description,
        ...(linksDirty ? { boardTokens: linked } : {}),
      });
      setSaved(updated);
      setName(updated.name);
      setSku(updated.sku ?? "");
      setDescription(updated.description);
      setLinked(updated.boardTokens ?? []);
      onSaved();
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  }
  async function reload() {
    setSaving(true);
    setError(false);
    try {
      const current = await creativeLibraryApi.product(product.token);
      setSaved(current);
      setName(current.name);
      setSku(current.sku ?? "");
      setDescription(current.description);
      setLinked(current.boardTokens ?? []);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  }
  return (
    <>
      <CreativeEditorDialog
        open={!upload && !newBoard && !preview}
        busy={saving}
        onClose={onClose}
        kind="products"
        title={saved.name}
        description={brand.name}
        testId="creative-product-profile"
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={saving}>
              {t("creativeLibrary.done")}
            </Button>
            {canEdit && (
              <Button
                variant="outline"
                onClick={() => void save()}
                disabled={!dirty || !name.trim() || saving}
                data-testid="product-details-save"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("creativeLibrary.saveProductDetails")}
              </Button>
            )}
            <Button
              variant="primary"
              disabled={
                dirty ||
                saving ||
                !hasProduct ||
                missing ||
                used.length > 4 ||
                (board !== "none" && !chosen)
              }
              data-testid="product-profile-create"
              onClick={() =>
                onUse(saved, {
                  ...(chosen ? { boardToken: chosen.token } : {}),
                  referenceTokens: selected,
                })
              }
            >
              {t("creativeLibrary.createWithProduct")}
            </Button>
          </>
        }
      >
        <CreativeEditorSection>
          <Label>{t("creativeLibrary.boardForCreation")}</Label>
          <Select value={board} onValueChange={setBoard} disabled={saving}>
            <SelectTrigger aria-label={t("creativeLibrary.boardForCreation")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                {t("creativeLibrary.noBoard")}
              </SelectItem>
              {applicable
                .filter((item) => linked.includes(item.token))
                .map((item) => (
                  <SelectItem key={item.token} value={item.token}>
                    {item.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Text size="sm">
            {t("creativeLibrary.creationReferences", { count: used.length })}
          </Text>
          <div className="flex flex-wrap gap-2">
            {used.map((token) => {
              const item = allImages.find((image) => image.token === token);
              return item ? (
                <div key={token} className="flex w-20 flex-col gap-1">
                  <PrivateImage
                    source={item.mediaUrl}
                    alt={item.title}
                    className="h-16 w-16 rounded-lg border border-border object-contain"
                  />
                  <Text size="xs" className="truncate" title={item.title}>
                    {item.title}
                  </Text>
                  <Text size="xs" color="muted">
                    {t(
                      chosen?.definition.referenceTokens.includes(token)
                        ? "creativeLibrary.includedByBoard"
                        : fixed.includes(token)
                          ? "creativeLibrary.includedByBrand"
                          : "creativeLibrary.selectedReference",
                    )}
                  </Text>
                </div>
              ) : (
                <AlertCircle key={token} className="h-5 w-5 text-destructive" />
              );
            })}
          </div>
          <Text size="xs" color="muted">
            {t(
              dirty
                ? "creativeLibrary.saveBeforeCreate"
                : missing
                  ? "creativeLibrary.productReferenceUnavailable"
                  : !hasProduct
                    ? "creativeLibrary.chooseProductPhoto"
                    : used.length > 4
                      ? "creativeLibrary.referenceLimitHint"
                      : "creativeLibrary.creationReferencesHelp",
            )}
          </Text>
          {!hasProduct && (
            <Button
              variant="outline"
              size="sm"
              className="self-start"
              onClick={() => setTab("images")}
            >
              {t("creativeLibrary.chooseImages")}
            </Button>
          )}
        </CreativeEditorSection>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-3 bg-background">
            <TabsTrigger value="details">
              {t("creativeLibrary.productDetailsTab")}
            </TabsTrigger>
            <TabsTrigger value="images">
              {t("creativeLibrary.productImagesTab")}
            </TabsTrigger>
            <TabsTrigger value="boards">
              {t("creativeLibrary.boards")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="details" className="pt-4">
            <CreativeEditorSection>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="profile-product-name">
                    {t("creativeLibrary.productName")}
                  </Label>
                  <Input
                    id="profile-product-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!canEdit || saving}
                    maxLength={255}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="profile-product-sku">
                    {t("creativeLibrary.sku")}
                  </Label>
                  <Input
                    id="profile-product-sku"
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                    disabled={!canEdit || saving}
                    maxLength={255}
                  />
                </div>
              </div>
              <Label htmlFor="profile-product-description">
                {t("creativeLibrary.productFacts")}
              </Label>
              <Textarea
                id="profile-product-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!canEdit || saving}
                maxLength={2000}
                rows={7}
                placeholder={t("creativeLibrary.productFactsPlaceholder")}
              />
              <Text size="xs" color="muted">
                {t("creativeLibrary.productFactsHelp")}
              </Text>
            </CreativeEditorSection>
          </TabsContent>
          <TabsContent value="images" className="flex flex-col gap-4 pt-4">
            {canEdit && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => setUpload("product")}
                >
                  <ImagePlus className="h-4 w-4" />
                  {t("creativeLibrary.addProductPhotos")}
                </Button>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => setUpload("style")}
                >
                  <Plus className="h-4 w-4" />
                  {t("creativeLibrary.addInspiration")}
                </Button>
              </div>
            )}
            <ProductGallery
              items={gallery}
              selected={selected}
              fixed={fixed}
              usedCount={used.length}
              onChange={setSelected}
              onPreview={setPreview}
              boardPinned={chosen?.definition.referenceTokens ?? []}
            />
          </TabsContent>
          <TabsContent value="boards" className="flex flex-col gap-4 pt-4">
            <Text size="sm" color="muted">
              {t("creativeLibrary.productBoardsHelp")}
            </Text>
            {(saved.unavailableBoardCount ?? 0) > 0 && (
              <Text size="xs" color="muted">
                {t("creativeLibrary.productBoardsUnavailable")}
              </Text>
            )}
            {applicable.map((item) => (
              <label
                key={item.token}
                className="flex items-start gap-3 rounded-xl border border-border bg-background p-3"
              >
                <Checkbox
                  checked={linked.includes(item.token)}
                  disabled={
                    !canEdit ||
                    saving ||
                    (saved.unavailableBoardCount ?? 0) > 0 ||
                    (!linked.includes(item.token) && linked.length >= 20)
                  }
                  onCheckedChange={(value) => {
                    setLinked(
                      value === true
                        ? [...linked, item.token]
                        : linked.filter((token) => token !== item.token),
                    );
                    if (value !== true && board === item.token)
                      setBoard("none");
                  }}
                />
                <span className="flex min-w-0 flex-col gap-1">
                  <Text size="sm">{item.name}</Text>
                  <Text size="xs" color="muted">
                    {item.channelTags.join(" · ")}
                  </Text>
                </span>
              </label>
            ))}
            {canEdit && (
              <Button
                variant="outline"
                className="gap-2 self-start"
                onClick={() => setNewBoard(true)}
              >
                <Plus className="h-4 w-4" />
                {t("creativeLibrary.newBoard")}
              </Button>
            )}
          </TabsContent>
        </Tabs>

        {error && (
          <div role="alert" className="flex items-center justify-between gap-2">
            <Text size="sm" className="text-destructive">
              {t("creativeLibrary.productSaveFailed")}
            </Text>
            <Button
              variant="outline"
              onClick={() => void reload()}
              disabled={saving}
            >
              {t("creativeLibrary.reload")}
            </Button>
          </div>
        )}
      </CreativeEditorDialog>
      <CreativeUpload
        open={Boolean(upload)}
        presetRole={upload ?? undefined}
        presetBrandToken={brand.token}
        presetProductToken={product.token}
        brands={[brand]}
        products={[saved]}
        onClose={() => setUpload(null)}
        onSaved={onSaved}
      />
      <CreativeDetail
        item={
          preview
            ? (allImages.find((item) => item.token === preview.token) ??
              preview)
            : null
        }
        brands={[brand]}
        products={[saved]}
        onClose={() => setPreview(null)}
        onSaved={onSaved}
      />
      <BoardEditor
        open={newBoard}
        board={null}
        brands={[brand]}
        creatives={allImages}
        onClose={() => setNewBoard(false)}
        onSaved={onSaved}
      />
    </>
  );
}
